import { motion, useReducedMotion } from 'framer-motion'
import { useIsMobile } from '../lib/useIsMobile'
import { useT } from '../i18n/LocaleContext'

type Promise = {
  title: string
  desc: string
  icon: 'invisible' | 'flexible' | 'human'
}

export default function Closing() {
  const isMobile = useIsMobile()
  const reduce = useReducedMotion()
  const { t } = useT()

  const PROMISES: Promise[] = [
    {
      title: t('closing.promiseInvisibleTitle'),
      desc: t('closing.promiseInvisibleDesc'),
      icon: 'invisible',
    },
    {
      title: t('closing.promiseFlexibleTitle'),
      desc: t('closing.promiseFlexibleDesc'),
      icon: 'flexible',
    },
    {
      title: t('closing.promiseHumanTitle'),
      desc: t('closing.promiseHumanDesc'),
      icon: 'human',
    },
  ]

  return (
    <section
      id="closing"
      className="relative isolate py-24 sm:py-28 overflow-hidden"
      aria-labelledby="closing-title"
    >
      {/* Background DA — same dot pattern + soft radial as Features/Carriers */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-40"
          style={{
            background: 'linear-gradient(to bottom, rgba(59,93,255,0.04), transparent)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />
        {/* Single ambient halo — static on mobile (mobile GPU friendly) */}
        {isMobile ? (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[420px] rounded-full opacity-50"
            style={{
              background:
                'radial-gradient(ellipse, rgba(59,93,255,0.13), rgba(59,93,255,0.03) 45%, transparent 75%)',
            }}
          />
        ) : (
          <motion.div
            animate={{ opacity: [0.4, 0.65, 0.4] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[420px] rounded-full"
            style={{
              background:
                'radial-gradient(ellipse, rgba(59,93,255,0.16), rgba(59,93,255,0.04) 45%, transparent 75%)',
              filter: 'blur(50px)',
            }}
          />
        )}
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        {/* Header — same pattern as Features/Carriers */}
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('closing.badge')}</span>
          </motion.div>

          <motion.h2
            id="closing-title"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-5 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(2rem,4.5vw,3.25rem)] leading-[1.12]"
          >
            {t('closing.headlinePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.04em] inline-block align-baseline">
              {t('closing.headlinePart2')}
            </span>
            .
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-5 text-base sm:text-lg text-ink-600 leading-relaxed"
          >
            {t('closing.subhead')}
          </motion.p>
        </div>

        {/* Panneau sombre premium — même matière que le bloc CTA final
            (gradient ink-950↔900, dot pattern blanc, halos bleus) : les deux
            moments sombres de la page partagent le même ADN. Les 3 engagements
            vivent en colonnes séparées par des hairlines blanches. */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 sm:mt-16 relative overflow-hidden isolate rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 px-7 py-12 sm:px-12 sm:py-14 ring-1 ring-white/10"
        >
          {/* dot pattern blanc masqué */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.22] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
              backgroundSize: '26px 26px',
              maskImage:
                'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }}
          />
          {/* halos bleus */}
          <div
            aria-hidden
            className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.4), transparent 70%)' }}
          />
          <div
            aria-hidden
            className="absolute -bottom-40 -left-28 w-[380px] h-[380px] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.25), transparent 70%)' }}
          />

          <div className="relative grid sm:grid-cols-3 sm:divide-x sm:divide-white/[0.08] gap-y-10 sm:gap-y-0">
            {PROMISES.map((p, i) => (
              <motion.div
                key={p.icon}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.65, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group text-center sm:text-left sm:px-8 first:sm:pl-0 last:sm:pr-0"
              >
                {/* Tuile gradient accent — même famille que les tuiles Services.
                    Centrée sur mobile (colonnes empilées), à gauche dès sm. */}
                <motion.div
                  whileHover={isMobile ? undefined : { y: -3 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="relative w-12 h-12 mx-auto sm:mx-0 rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_14px_36px_-10px_rgba(59,93,255,0.8)]"
                >
                  <span className="absolute inset-0 rounded-xl ring-1 ring-white/25" />
                  <PromiseGlyph name={p.icon} animate={!isMobile && !reduce} size={22} />
                </motion.div>

                <h3 className="mt-5 text-lg font-semibold tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-ink-300">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

/**
 * Lightweight inline SVG glyphs, parametric (taille/graisse) pour servir à la
 * fois de picto dans la tuile et de filigrane géant dans la scène.
 */
function PromiseGlyph({
  name,
  animate,
  size = 22,
  strokeWidth = 2,
  className,
}: {
  name: Promise['icon']
  animate: boolean
  size?: number
  strokeWidth?: number
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {name === 'invisible' && (
        <motion.g
          animate={animate ? { opacity: [1, 0.55, 1] } : { opacity: 1 }}
          transition={{ duration: 3, repeat: animate ? Infinity : 0, ease: 'easeInOut' }}
        >
          <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </motion.g>
      )}
      {name === 'flexible' && (
        <motion.g
          animate={animate ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          transition={{ duration: 2.6, repeat: animate ? Infinity : 0, ease: 'easeInOut' }}
          style={{ transformOrigin: '12px 12px' }}
        >
          <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" />
        </motion.g>
      )}
      {name === 'human' && (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
          <motion.circle
            cx="20"
            cy="5"
            r="2"
            fill="currentColor"
            stroke="none"
            animate={animate ? { opacity: [0.4, 1, 0.4] } : { opacity: 1 }}
            transition={{ duration: 1.6, repeat: animate ? Infinity : 0, ease: 'easeInOut' }}
          />
        </>
      )}
    </svg>
  )
}
